import { faCamera } from "@fortawesome/free-solid-svg-icons/faCamera";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import isString from "lodash/isString";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Rounded } from "../theme/Rounded";
import { Spacing } from "../theme/Spacing";
import { getMediaUrl } from "../utils/Media";
import { AtiraIcon } from "./AtiraIcon";
import { Button } from "./Button";
import { Flex } from "./Flex";
import { ImageCreateEdit } from "./ImageCreateEdit";
import { Text } from "./Text";
import ImgCrop from "antd-img-crop";
import { Tooltip, Upload, Message } from "../utils/local-antd";
import type {
  UploadFile,
  UploadProps,
  UploadChangeParam,
} from "../utils/local-antd";
import RequiredSpan from "./RequiredSpan";

type UploadListType = "text" | "picture" | "picture-card" | "picture-circle";

const StyledTitle = styled(Text)`
  font-size: initial;
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.darkerSub};
`;

const UploadWrapper = styled(Flex)<{
  listType: UploadListType;
  isFileUploaded: boolean;
}>`
  justify-content: center;

  .ant-upload-select {
    width: 100%;
    display: block;
  }

  .ant-image {
    height: 100% !important;
  }

  .ant-image-img {
    height: ${({ listType }) =>
      listType === "picture-circle" ? "100%" : "auto"};
    aspect-ratio: ${({ listType }) =>
      listType === "picture-circle" ? "1 / 1" : "auto"};
  }

  ${({ listType }) =>
    listType === "picture-card"
      ? `
          & > div {
            width: 100%;
          }
        
          .upload-picture .ant-upload {
            overflow: hidden;
            min-height: 12rem;
          }
        
          .ant-upload-wrapper,
          .upload-picture .ant-upload {
            width: 100% !important;
          }

          .ant-image-img {
            width: auto !important;
            max-width: 100% !important;
            max-height: 12rem;
          }
            `
      : ""}
`;

const RemoveButton = styled(Button)`
  border-radius: ${Rounded.circle};
  padding: calc(${Spacing.s} / 2);
  width: 2rem !important;
  height: 2rem !important;
  margin: 0;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const uploadProps: UploadProps = {
  name: "file",
  defaultFileList: [],
  beforeUpload: (file) => file,
  onChange(info) {
    if (info.file.status === "done") {
      Message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === "error") {
      Message.error(`${info.file.name} file upload failed.`);
    }
  },
};

type ImageUploadProps = {
  onChange: ATVoidFunction<UploadFile | null>;
  defaultFiles?: UploadFile[];
  listType?: UploadListType;
  showUploadList?: boolean;
  title?: string;
  required?: boolean;
  errorMessage?: string;
  valid?: boolean;
  image: string | UploadFile | null;
  dataTestId?: string;
  showAspectRatioTooltip?: boolean;
  imageStyles?: React.CSSProperties;
} & UploadProps & {
    cropProps?: {
      aspectSlider?: boolean;
      aspectRatio?: number;
      quality?: number;
      cropShape?: "round" | "rect";
      grid?: boolean;
      onModalOk?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
      modalOk?: string;
      modalTitle?: string;
    };
  };

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onChange,
  defaultFiles,
  listType = "picture-circle",
  showUploadList = false,
  required = false,
  valid = true,
  title,
  errorMessage,
  image,
  dataTestId,
  showAspectRatioTooltip = true,
  cropProps,
  imageStyles,
  ...props
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const uploadComponentRef = React.useRef<any>(null);

  const { t } = useTranslation();

  const handleChange = useCallback(
    (info: UploadChangeParam<UploadFile<any>>) => {
      uploadProps.onChange!(info);
      if (info.file.status !== "removed") {
        onChange(info.file);

        setFileList([info.file]);
      }
    },
    [onChange],
  );

  const handleCropOk = useCallback(
    (f: UploadFile) =>
      handleChange({ file: f } as UploadChangeParam<UploadFile>),
    [handleChange],
  );

  const handleRemove = () => {
    onChange(null);
    setFileList([]);
  };

  useEffect(() => {
    if (!image) {
      setFileList([]);
      return;
    }

    if (isString(image)) {
      const imageUrl = getMediaUrl(image);

      if (imageUrl) {
        setFileList([
          {
            uid: image,
            name: "image.jpg",
            status: "done",
            url: imageUrl,
          },
        ]);
      }
    } else if (image instanceof File) {
      setFileList([
        {
          uid: image.name,
          name: image.name,
          status: "done",
          url: URL.createObjectURL(image),
        },
      ]);
    }
  }, [image]);

  return (
    <Flex flexDirection="column" gap="s">
      {title ? (
        <StyledTitle>
          {title}
          {required ? (
            <RequiredSpan />
          ) : (
            <OptionalSpan> ({t("common.optional")})</OptionalSpan>
          )}
        </StyledTitle>
      ) : null}

      <UploadWrapper listType={listType} isFileUploaded={fileList.length > 0}>
        <Tooltip
          title={showAspectRatioTooltip ? t("common.imageUploadTooltip") : ""}
        >
          <ImgCrop
            quality={1}
            aspectSlider
            showReset
            // @ts-ignore
            onModalOk={handleCropOk}
            modalOk={t("common.save")}
            modalTitle={t("common.editImage")}
            resetText={t("common.reset")}
            {...cropProps}
          >
            <Upload
              showUploadList={showUploadList}
              listType={listType}
              {...uploadProps}
              maxCount={1}
              defaultFileList={fileList}
              fileList={fileList}
              className="upload-picture"
              ref={uploadComponentRef}
              style={{ padding: Spacing.m }}
              {...props}
            >
              {fileList.length ? (
                <ImageCreateEdit
                  src={isString(image) ? image : fileList[0]?.url}
                  style={{ ...imageStyles }}
                />
              ) : (
                <AtiraIcon icon={faCamera} size="2x" color="main" />
              )}
            </Upload>
          </ImgCrop>
        </Tooltip>
      </UploadWrapper>

      <Flex gap="s" justifyContent="center" marginTop="s">
        <Tooltip title={t("common.remove")}>
          <RemoveButton
            icon={faTrash}
            onClick={handleRemove}
            iconWidth="xs"
            disabled={!fileList.length}
          />
        </Tooltip>
      </Flex>
      {errorMessage && !valid ? (
        <Text color="danger">{errorMessage}</Text>
      ) : null}
    </Flex>
  );
};
